import React, { useContext, useEffect, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import AuthContext from 'context/AuthContext'
import OSearch from 'components/reusable/OSearch'
import OButton from 'components/reusable/OButton'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import CustomSelect from 'components/reusable/CustomSelect'
import TrainingTab from './TrainingTab.js'
import CertificationTab from './CertificationTab'

const tabBtnClass = 'inline-block py-3 px-8 text-md xl:text-lg font-semibold border-b-2'
const tabActiveClass = 'border-blue-600 text-blue-600'
const tabBorderNoneClass = 'border-transparent'

// Parent: owns the tab switch + the shared filter bar, passes filters down.
function Training() {
    const { t } = useTranslation()
    const { updatePageName } = useContext(AuthContext)
    const [tab, setTab] = useState('training')
    const [searchTerm, setSearchTerm] = useState('')
    const [sortType, setSortType] = useState('')
    const [page, setPage] = useState(1) // pagination used by the Training tab
    const [filterData, setFilterData] = useState({
        startDate: '',
        endDate: '',
        verification: '',
        isReset: false
    })

    useEffect(() => {
        updatePageName('Training & Competency')
    }, [])

    const isCertification = tab === 'certification'

    const resetFilters = () => {
        setSearchTerm('')
        setSortType('')
        setPage(1)
        setFilterData({
            startDate: '',
            endDate: '',
            verification: '',
            isReset: true
        })
    }

    return (
        <div className='md:px-8 py-4 px-4'>
            <div className='py-3 my-2'>
                <ul className='border-b flex'>
                    <li className='mr-0 xl:mr-5'>
                        <button
                            type='button'
                            onClick={() => setTab('training')}
                            className={classNames({
                                [tabBtnClass]: true,
                                [tabActiveClass]: tab === 'training',
                                [tabBorderNoneClass]: tab !== 'training'
                            })}
                        >
                            Training
                        </button>
                    </li>
                    <li className='mx-0 xl:mx-5'>
                        <button
                            type='button'
                            onClick={() => setTab('certification')}
                            className={classNames({
                                [tabBtnClass]: true,
                                [tabActiveClass]: tab === 'certification',
                                [tabBorderNoneClass]: tab !== 'certification'
                            })}
                        >
                            Certification
                        </button>
                    </li>
                </ul>
            </div>
            <div className='bg-white border rounded-lg border-[#E9EDF9]'>
                <form className='border-b border-b-[#E3E3E3] gap-2 2xl:flex px-4 py-3'>
                    <div className='flex items-center flex-wrap'>
                        <div className='relative flex mb-3 items-center'>
                            <OSearch
                                placeholder='Employee name, ID'
                                searchTerm={searchTerm}
                                setSearchTerm={(value) => {
                                    setPage(1)
                                    setSearchTerm(value)
                                }}
                            />
                        </div>
                        <ODateRangePicker
                            handleDateChange={(startDate, endDate) => {
                                setPage(1)
                                setFilterData((current) => ({
                                    ...current,
                                    startDate: startDate || '',
                                    endDate: endDate || '',
                                    isReset: false
                                }))
                            }}
                            setIsReset={() => setFilterData((current) => ({ ...current, isReset: false }))}
                            isReset={filterData.isReset}
                            startDate={filterData.startDate}
                            endDate={filterData.endDate}
                        />
                        {isCertification && (
                            <CustomSelect
                                id='verification'
                                name='verification'
                                options={[
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'verified', label: 'Verified' },
                                    { value: 'rejected', label: 'Rejected' }
                                ]}
                                placeholder='All'
                                value={filterData.verification}
                                onChange={(event) => setFilterData((current) => ({ ...current, verification: event.target.value }))}
                                t={t}
                            />
                        )}
                        <CustomSelect
                            id='sortOrder'
                            name='sortOrder'
                            options={[
                                { value: 'asc', label: 'A-Z' },
                                { value: 'desc', label: 'Z-A' }
                            ]}
                            placeholder='Sort by'
                            value={sortType}
                            onChange={(event) => {
                                setPage(1)
                                setSortType(event.target.value)
                            }}
                            t={t}
                        />
                        <OButton
                            label='Reset'
                            extraClasses='ml-3 mb-3'
                            style={{ borderRadius: '0.5rem' }}
                            type='button'
                            onClick={resetFilters}
                        />
                    </div>
                </form>

                {tab === 'training' && (
                    <TrainingTab
                        searchTerm={searchTerm}
                        sortType={sortType}
                        filterData={filterData}
                        page={page}
                        setPage={setPage}
                    />
                )}

                {tab === 'certification' && (
                    <CertificationTab
                        searchTerm={searchTerm}
                        sortType={sortType}
                        filterData={filterData}
                    />
                )}
            </div>
        </div>
    )
}

export default Training